import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, GraduationCap, Loader2, Send, Sparkles } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { useAuth } from '@/lib/auth-store';
import { useCoursesQuery } from '@/hooks/queries/useCourses';
import { api, ApiError, type TutorSourceShape } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Tutoring Center — Phase-5 surface, with Phase-6 course-grounded RAG.
 *
 * Multi-turn one-on-one tutor that hits POST /ai/tutor. Different from the
 * Companion chat (which uses /ai/eduborg-respond) by tone + intent: the
 * tutor's system prompt steers it toward step-by-step explanation, never
 * giving away graded-assignment answers, and matching the student's grade
 * level.
 *
 * Course selector: pick a course and the tutor pulls the most relevant
 * lesson snippets from that course's FTS5 index into its prompt as
 * "course context". Picking "General help" sends no courseId and the
 * tutor reasons from its own training data only.
 *
 * Layout: full-page chat surface. Header + scrollable transcript +
 * fixed-bottom composer. Costs 2 AI credits per turn. Simulate mode shows
 * the same placeholder reply as the rest of the AI surfaces.
 */
type Turn = {
  role: 'user' | 'assistant';
  content: string;
  simulated?: boolean;
  sources?: TutorSourceShape[];
};

export function Tutor() {
  const { state } = useAuth();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Empty string sentinel = "General help" (no courseId; tutor reasons from
  // its own training data only). Otherwise this is the course UUID.
  const [courseId, setCourseId] = useState<string>('');
  const scrollerRef = useRef<HTMLDivElement>(null);

  const coursesQuery = useCoursesQuery(state.status === 'authenticated' ? state.token : '');
  const courses = coursesQuery.data?.courses ?? [];

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight });
  }, [turns.length, sending]);

  if (state.status !== 'authenticated') return null;
  const token = state.token;

  async function send(e: FormEvent) {
    e.preventDefault();
    const question = draft.trim();
    if (!question || sending) return;
    setError(null);
    setSending(true);
    setDraft('');
    // Capture history *before* mutating, so the tutor sees turns up to but
    // not including the new question (the API adds it itself).
    const history = turns.slice(-12).map((t) => ({ role: t.role, content: t.content }));
    setTurns((t) => [...t, { role: 'user', content: question }]);
    try {
      const res = await api.ai.tutor(token, {
        question,
        history,
        ...(courseId ? { courseId } : {}),
      });
      setTurns((t) => [
        ...t,
        {
          role: 'assistant',
          content: res.reply,
          ...(res.simulated ? { simulated: true } : {}),
          ...(res.sources && res.sources.length ? { sources: res.sources } : {}),
        },
      ]);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ai_access_denied') {
        setError("You're out of AI credits for this period.");
      } else {
        setError("Couldn't reach the tutor. Try again in a moment.");
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-[hsl(220,40%,98%)] via-background to-secondary/40">
      <AppHeader />

      <main className="container flex flex-1 flex-col gap-4 py-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              <Link to="/dashboard" className="hover:text-foreground">
                Dashboard
              </Link>
              <span className="mx-1">/</span>
              <span>Tutor</span>
            </div>
            <h1 className="flex items-center gap-2 font-display text-3xl font-bold tracking-tight">
              <GraduationCap className="h-7 w-7 text-accent" /> Tutoring Center
            </h1>
            <p className="max-w-2xl text-sm text-muted-foreground">
              EduBorg explains concepts step by step, asks check-for-understanding questions, and
              matches your level. It won't hand you assignment answers — it'll guide you to find
              them yourself.
            </p>
          </div>

          {/*
            Course selector — when set, the tutor pulls the most relevant
            lesson snippets from that course and includes them in its
            prompt. Default is "General help" (no course context).
          */}
          <label className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-sm">
            <BookOpen className="h-4 w-4 text-muted-foreground" aria-hidden />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Course</span>
            <select
              value={courseId}
              onChange={(e) => setCourseId(e.target.value)}
              className="rounded bg-transparent text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/30"
              aria-label="Course context for the tutor"
            >
              <option value="">General help (no course)</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </label>
        </header>

        <section className="flex flex-1 flex-col rounded-2xl border border-border bg-card">
          <div ref={scrollerRef} className="flex-1 space-y-3 overflow-y-auto p-5">
            {turns.length === 0 && !sending ? (
              <EmptyTutor />
            ) : (
              turns.map((t, i) => (
                <article
                  key={i}
                  className={cn(
                    'max-w-[85%] rounded-2xl px-4 py-3 text-sm',
                    t.role === 'user'
                      ? 'ml-auto bg-primary text-primary-foreground'
                      : 'bg-secondary text-foreground',
                  )}
                >
                  <div className="whitespace-pre-wrap break-words">{t.content}</div>
                  {t.sources && t.sources.length > 0 && (
                    <div className="mt-3 space-y-1.5 border-t border-border/40 pt-2">
                      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <BookOpen className="h-3 w-3" aria-hidden />
                        Drawn from your course
                      </div>
                      <ul className="space-y-1">
                        {t.sources.map((s, j) => (
                          <li key={s.blockId} className="text-[11px] text-muted-foreground">
                            <span className="font-medium text-foreground">
                              [{j + 1}] {s.lessonTitle}
                            </span>
                            <span className="ml-1.5">— {s.snippet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {t.simulated && (
                    <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                      simulated · set AIML_API_KEY
                    </div>
                  )}
                </article>
              ))
            )}
            {sending && (
              <div className="text-xs italic text-muted-foreground">EduBorg is thinking…</div>
            )}
          </div>

          <form
            onSubmit={send}
            className="flex items-end gap-2 border-t border-border bg-card/50 p-3"
          >
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Ask EduBorg about anything you're studying."
              rows={2}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send(e as unknown as FormEvent);
                }
              }}
              className="flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <button
              type="submit"
              disabled={!draft.trim() || sending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Ask
            </button>
          </form>

          {error && (
            <div className="border-t border-destructive/30 bg-destructive/5 px-4 py-2 text-xs text-destructive">
              {error}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function EmptyTutor() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
      <Sparkles className="h-8 w-8 text-accent" aria-hidden />
      <p className="max-w-md text-sm text-muted-foreground">
        Type a question to start. Try things like "explain mitosis to a 9th grader" or "I'm stuck on
        a problem about projectile motion — walk me through it."
      </p>
    </div>
  );
}
